import { apiClient, getApiBaseUrl } from './apiClient';
import { Counterparty } from '../types/counterparty';

export interface IntegrationConfig {
  [key: string]: string;
}

export interface WFirmaImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  errorMessages: string[];
}

export interface WFirmaMatchOnlyResult {
  linked: number;
  skippedAmbiguous: number;
  skippedNoMatch: number;
  errors: number;
  errorMessages: string[];
}

export interface WFirmaContractorCandidate {
  wfirmaId: number;
  name: string;
  nip: string;
  city: string;
  country: string;
  taxIdType: string;
}

export interface BulkInvoiceSyncResult {
  totalSelected: number;
  success: number;
  skippedAlreadySynced: number;
  failed: number;
  errorMessages: string[];
}

export interface BulkInvoiceResetResult {
  totalSelected: number;
  resetToDraft: number;
  skippedByStatus: number;
}

export interface PullInvoiceMetadataFromWfirmaResult {
  totalSelected: number;
  updated: number;
  skippedUnchanged: number;
  failed: number;
  errorMessages: string[];
}

export interface VatCodesCheckResult {
  status: string;
  message: string;
  totalCount: number;
  hasRate23: boolean;
  hasRate8: boolean;
  hasRate0: boolean;
  hasZw: boolean;
  hasNp: boolean;
  hasNpue: boolean;
  vatCodes: Array<{
    wfirmaId: number;
    code?: string;
    name?: string;
    rate?: number;
    isEu?: boolean;
  }>;
}

export interface SyncInvoicesPeriodRequest {
  startDate: string;
  endDate: string;
  forceResync?: boolean;
  /** ISSUE — дата выставления, SALE — дата продажи */
  dateField?: 'ISSUE' | 'SALE';
  /** Список статусов для операций массового сброса в черновик */
  statuses?: string[];
}

export interface PullInvoiceMetadataFromWfirmaRequest {
  startDate: string;
  endDate: string;
  dateField?: 'ISSUE' | 'SALE';
  syncKsefNumbers?: boolean;
  syncInvoiceNumbersFromWfirma?: boolean;
}

export interface BulkInvoiceSyncProgressEvent {
  phase: string;
  total: number;
  processed: number;
  success: number;
  skippedAlreadySynced: number;
  failed: number;
  currentInvoiceNumber?: string | null;
  result?: BulkInvoiceSyncResult;
}

export interface PullInvoiceMetadataProgressEvent {
  phase: string;
  total: number;
  processed: number;
  updated: number;
  skippedUnchanged: number;
  failed: number;
  currentInvoiceNumber?: string | null;
  result?: PullInvoiceMetadataFromWfirmaResult;
}

/**
 * Читает SSE (data: JSON). После каждого события делает yield в macrotask,
 * иначе React 18 схлопывает все setState из одного сетевого чанка — прогресс не виден.
 *
 * В конце потока допарсивает буфер без обязательного завершающего \\n\\n (иначе теряется событие done).
 * Поддерживает \\r\\n.
 */
async function readSseJsonStream(response: Response, onData: (obj: unknown) => void): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Пустой ответ сервера');
  }
  const decoder = new TextDecoder();
  let buffer = '';

  const processBlock = async (block: string) => {
    if (!block.trim()) {
      return;
    }
    for (const line of block.split(/\r?\n/)) {
      if (!line.startsWith('data:')) {
        continue;
      }
      const raw = line.slice(5).trim();
      if (!raw) {
        continue;
      }
      onData(JSON.parse(raw));
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  };

  const splitNextBlock = (): string | null => {
    let sep = buffer.indexOf('\n\n');
    let len = 2;
    if (sep < 0) {
      sep = buffer.indexOf('\r\n\r\n');
      len = 4;
    }
    if (sep < 0) {
      return null;
    }
    const block = buffer.slice(0, sep);
    buffer = buffer.slice(sep + len);
    return block;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        buffer += decoder.decode(value ?? new Uint8Array(), { stream: false });
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      for (;;) {
        const block = splitNextBlock();
        if (!block) {
          break;
        }
        await processBlock(block);
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }

  for (;;) {
    const block = splitNextBlock();
    if (!block) {
      break;
    }
    await processBlock(block);
  }
  if (buffer.trim()) {
    await processBlock(buffer);
  }
}

export const integrationService = {
  // Получить список всех типов интеграций для компании
  getIntegrationTypes: async (companyId: string): Promise<string[]> => {
    const response = await apiClient.get(`/companies/${companyId}/integrations`);
    return response.data;
  },

  // Получить конфигурацию интеграции
  getIntegrationConfig: async (companyId: string, integrationType: string): Promise<IntegrationConfig> => {
    const response = await apiClient.get(`/companies/${companyId}/integrations/${integrationType}/config`);
    return response.data;
  },

  // Сохранить конфигурацию интеграции
  saveIntegrationConfig: async (companyId: string, integrationType: string, config: IntegrationConfig): Promise<void> => {
    await apiClient.post(`/companies/${companyId}/integrations/${integrationType}/config`, config);
  },

  // Удалить конфигурацию интеграции
  deleteIntegrationConfig: async (companyId: string, integrationType: string): Promise<void> => {
    await apiClient.delete(`/companies/${companyId}/integrations/${integrationType}/config`);
  },

  /**
   * Полностью настроенная интеграция wFirma (ключи + company_id), а не просто факт записи в БД.
   * Иначе при «обрывочной» конфигурации открывалась модалка синхронизации контрагента без реальной интеграции.
   */
  hasWFirmaConfig: async (companyId: string): Promise<boolean> => {
    try {
      const response = await apiClient.get<{ active?: boolean }>(
        `/companies/${companyId}/wfirma/integration-active`
      );
      return response.data?.active === true;
    } catch (error: any) {
      if (error.response?.status === 403 || error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  },

  // Импортировать контрагентов из wFirma
  importContractorsFromWFirma: async (companyId: string): Promise<WFirmaImportResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/import-contractors`);
    return response.data;
  },

  /** Только сопоставление: проставить wfirma_id без создания новых контрагентов */
  matchOnlyContractorsFromWFirma: async (companyId: string): Promise<WFirmaMatchOnlyResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/import-contractors/match-only`);
    return response.data;
  },

  /** Поиск кандидатов в wFirma для ручной привязки */
  searchWFirmaContractorCandidates: async (
    companyId: string,
    params: { name?: string; nip?: string; vat?: string }
  ): Promise<WFirmaContractorCandidate[]> => {
    const response = await apiClient.get(`/companies/${companyId}/wfirma/contractor-candidates`, {
      params
    });
    return response.data;
  },

  /** Привязать локального контрагента к записи wFirma */
  linkCounterpartyToWfirma: async (
    companyId: string,
    counterpartyId: string,
    wfirmaId: number
  ): Promise<Counterparty> => {
    const response = await apiClient.put(
      `/companies/${companyId}/wfirma/counterparties/${counterpartyId}/wfirma-link`,
      { wfirmaId }
    );
    return response.data;
  },

  /** Массовая отправка фактур в wFirma за период */
  syncInvoicesPeriod: async (companyId: string, body: SyncInvoicesPeriodRequest): Promise<BulkInvoiceSyncResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/invoices/sync-period`, body);
    return response.data;
  },

  /**
   * Массовая отправка с прогрессом (SSE). События: phase start | tick | done.
   */
  syncInvoicesPeriodStream: async (
    companyId: string,
    body: SyncInvoicesPeriodRequest,
    onEvent: (e: BulkInvoiceSyncProgressEvent) => void
  ): Promise<BulkInvoiceSyncResult> => {
    const base = getApiBaseUrl();
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${base}/companies/${companyId}/wfirma/invoices/sync-period-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const text = await res.text();
      let msg = text;
      try {
        const j = JSON.parse(text) as { message?: string };
        if (j?.message) {
          msg = j.message;
        }
      } catch {
        /* ignore */
      }
      throw new Error(msg || `HTTP ${res.status}`);
    }
    let final: BulkInvoiceSyncResult | undefined;
    try {
      await readSseJsonStream(res, (data) => {
        const e = data as BulkInvoiceSyncProgressEvent;
        onEvent(e);
        if (e.phase === 'done' && e.result) {
          final = e.result;
        }
      });
    } catch (err) {
      if (!final) {
        throw err instanceof Error ? err : new Error(String(err));
      }
      /* После emitter.complete() некоторые браузеры дают ошибку чтения, хотя done уже пришёл */
    }
    if (!final) {
      throw new Error('Поток завершился без итогового результата');
    }
    return final;
  },

  pullInvoiceMetadataPeriod: async (
    companyId: string,
    body: PullInvoiceMetadataFromWfirmaRequest
  ): Promise<PullInvoiceMetadataFromWfirmaResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/invoices/pull-metadata-period`, body);
    return response.data;
  },

  /** Ручной дотяг метаданных для последних отправленных фактур без KSeF */
  pullLatestMissingKsefInvoices: async (
    companyId: string,
    limit = 20
  ): Promise<PullInvoiceMetadataFromWfirmaResult> => {
    const response = await apiClient.post(
      `/companies/${companyId}/wfirma/invoices/pull-metadata-latest-missing-ksef?limit=${limit}`
    );
    return response.data;
  },

  pullInvoiceMetadataPeriodStream: async (
    companyId: string,
    body: PullInvoiceMetadataFromWfirmaRequest,
    onEvent: (e: PullInvoiceMetadataProgressEvent) => void
  ): Promise<PullInvoiceMetadataFromWfirmaResult> => {
    const base = getApiBaseUrl();
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${base}/companies/${companyId}/wfirma/invoices/pull-metadata-period-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const text = await res.text();
      let msg = text;
      try {
        const j = JSON.parse(text) as { message?: string };
        if (j?.message) {
          msg = j.message;
        }
      } catch {
        /* ignore */
      }
      throw new Error(msg || `HTTP ${res.status}`);
    }
    let final: PullInvoiceMetadataFromWfirmaResult | undefined;
    try {
      await readSseJsonStream(res, (data) => {
        const e = data as PullInvoiceMetadataProgressEvent;
        onEvent(e);
        if (e.phase === 'done' && e.result) {
          final = e.result;
        }
      });
    } catch (err) {
      if (!final) {
        throw err instanceof Error ? err : new Error(String(err));
      }
    }
    if (!final) {
      throw new Error('Поток завершился без итогового результата');
    }
    return final;
  },

  /** Массовый перевод отправленных фактур обратно в черновики */
  resetInvoicesToDraftPeriod: async (
    companyId: string,
    body: SyncInvoicesPeriodRequest
  ): Promise<BulkInvoiceResetResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/invoices/reset-to-draft-period`, body);
    return response.data;
  },

  /** Обновить VAT коды из wFirma и проверить базовые ставки/коды */
  refreshAndCheckVatCodes: async (companyId: string): Promise<VatCodesCheckResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/vat-codes/refresh-check`);
    return response.data;
  },

  /** PDF фактуры, сгенерированный в wFirma (нужны wfirmaId и настроенная интеграция) */
  downloadInvoicePdfFromWfirma: async (
    companyId: string,
    invoiceId: string,
    invoiceNumber: string,
    clientName: string
  ): Promise<void> => {
    const response = await apiClient.get(`/companies/${companyId}/wfirma/invoices/${invoiceId}/pdf`, {
      responseType: 'blob'
    });
    const contentType = (response.headers['content-type'] || '').toLowerCase();
    if (contentType.includes('application/json')) {
      const text = await (response.data as Blob).text();
      let msg = 'Download failed';
      try {
        const j = JSON.parse(text) as { message?: string };
        if (j?.message) msg = j.message;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const cleanClient = clientName.replace(/[^a-zA-Z0-9а-яА-ЯёЁіІїЇąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s-]/g, '').replace(/\s+/g, '-');
    const cleanNum = invoiceNumber.replace(/[^a-zA-Z0-9._-]/g, '_');
    link.setAttribute('download', `wfirma-${cleanNum}-${cleanClient || 'invoice'}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};
